import React, { ReactNode } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import KenColors from './kenColors';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    fullHD: true;
    ultraHD: true;
  }
}

interface GlobalThemeProviderProps {
  children: ReactNode;
}

const theme = createTheme({
  palette: {
    KenColors: { ...KenColors },
    // text: {
    //   primary: '#ff7961',   // main body text color
    //   secondary: '#7A869A', // lighter text color
    // },
    primary: {
      light: '#757ce8',
      main: KenColors?.ClientPrimaryII,
      dark: '#F7A703',
      contrastText: '#fff',
    },
    secondary: {
      light: '#ff7961',
      main: '#f44336',
      dark: '#ba000d',
      contrastText: '#000',
    },
    error: {
      main: '#E75547',
    },
    success: {
      main: '#1BBE75',
    },
    card: {
      backgroundColor: '#F7F8FB',
      hover: '#EDF2FF',
    },
    labels: {
      main: '#373A45',
      light: '#7A869A',
      medium: '#505F79',
    },
  },
  typography: {
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontFamily: ['GeneralSans'].join(','),
    body1: {
      fontSize: '16',
    },
    body2: {
      fontSize: 18,
      fontWeight: 500,
    },
    caption: {
      fontSize: '14px',
    },
    h1: {
      fontSize: '48px',
    },
    h2: {
      fontSize: '40px',
    },
    h3: {
      fontSize: '32px',
    },
    h4: {
      fontSize: '24px',
    },
    h5: {
      fontSize: '16px',
    },
    h6: {
      fontSize: '8px',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            backgroundColor: '#E0E0E0',
            color: '#9E9E9E',
            opacity: 0.6,
          },
        },
        contained: {
          boxShadow: 'none',
          borderRadius: '8px',
          transition: 'background-color 0.25s ease, color 0.25s ease, border-color 0.25s ease',
        },
      },
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            textTransform: 'capitalize',
            background: KenColors.ClientPrimary,
            borderRadius: '8px',
            color: '#373A45',
            '&:hover': {
              background: '#FFC73B',
              color: '#373A45',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              opacity: 0.6,
              background: KenColors.ClientPrimary,
              color: '#373A45',
            },

          },
        },
          {
          props: { variant: 'contained', color: 'secondary' },
          style: {
            textTransform: 'capitalize',
            background: "#ffffff",
            border: "1px solid #F7A703",
            borderRadius: '8px',
            color: '#373A45',
            '&:hover': {
              background: KenColors?.ClientPrimaryBg,
              color: '#373A45',
              border: `1px solid ${KenColors?.ClientPrimaryII}`,
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: KenColors?.ClientPrimaryBg,

              color: '#373A45',

              opacity: 0.6,
            },
          },

        },

        {
          props: { variant: 'contained', color: 'info' },
          style: {
            textTransform: 'capitalize',
            background: "#E8EBF3",
            borderRadius: '8px',
            color: '#373A45',
            '&:hover': {
              backgroundColor: '#E8EBF3', // same as base
              color: '#373A45',            // same as base
              boxShadow: 'none',           // remove hover shadow
            },
            '&.Mui-disabled': {
              opacity: 0.6,
              background: "#E8EBF3",
              color: '#373A45',

            },
          },
        },
      
        {
          props: { variant: 'contained', color: 'error' },
          style: {
            backgroundColor: "#FBEAEA",
            borderRadius: '8px',
            color: '#962121',
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: '#FBEAEA', // same as base
              color: '#962121',            // same as base
              boxShadow: 'none',           // remove hover shadow
            },
          },
        },
        {
          props: { variant: 'contained', color: 'inherit' },
          style: {
            border: '1px solid #FAB400',
            color: '#373A45',
            textTransform: 'capitalize',
            backgroundColor: "#ffffff",
            '&:hover': {
              backgroundColor: '#ffffff', // same as base
              color: '#373A45',            // same as base
              boxShadow: 'none',           // remove hover shadow
            },

          },
        },
        {
          props: { variant: 'text' },
          style: {
            textTransform: 'capitalize',
            color: "#373A45"
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            textTransform: 'capitalize',
          },
        },
      ],
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#A0A4AD', // default (unchecked) color
          '&.Mui-checked': {
            color: `${KenColors.ClientPrimary} !important`, // checked color
          },
          '&.Mui-disabled': {
            color: '#C8CBD0', // optional: disabled color
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0, // Extra small devices (phones)
      sm: 576, // Small devices (landscape phones)
      md: 768, // Medium devices (tablets)
      lg: 992, // Large devices (desktops)
      xl: 1200, // Extra large devices (large desktops)
      xxl: 1440, // Extra wide desktops (e.g., 1440px width monitors)
      fullHD: 1710, // Full HD (1920px width) or larger screens like wide desktop monitors
      ultraHD: 1920, // 4k or larger monitors
    },
  },
});

const GlobalThemeProvider: React.FC<GlobalThemeProviderProps> = ({ children }) => {
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export default GlobalThemeProvider;
